import { useCallback, useEffect, useState } from "react";
import { TimeInput } from "@/components/ui/time-input";
import { Link } from "react-router-dom";
import { api, unwrapObject } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { PageHeader } from "@/components/patterns";
import { ErrorState } from "@/components/patterns/States";
import { FormSkeleton } from "@/components/ui/page-skeletons";
import { PushDevices } from "@/components/push/PushDevices";

type Prefs = {
  events: { key: string; label: string; in_app: boolean; email: boolean; whatsapp: boolean; push: boolean }[];
  quiet_hours: { enabled: boolean; start: string; end: string };
  morning_summary: boolean;
  whatsapp_ready: boolean;
  managed_by_admin?: number;
  push_ready: boolean;
};
type Change = { events?: Record<string, { in_app?: boolean; email?: boolean; whatsapp?: boolean; push?: boolean }>; quiet_hours?: { enabled?: boolean; start?: string; end?: string }; morning_summary?: boolean };

const CHANNELS = [
  { key: "in_app", label: "In app", say: "in the app" },
  { key: "push", label: "Push", say: "as a push" },
  { key: "email", label: "Email", say: "by email" },
  { key: "whatsapp", label: "WhatsApp", say: "on WhatsApp" },
] as const;

/** Settings → Notifications: how each person hears about things (in the app, as a push, by email, on WhatsApp), and this device */
export function MyNotificationsPage({ embedded = false }: { embedded?: boolean } = {}) {
  const { hasPermission } = useAuth();
  const { success, error } = useToast();
  const [prefs, setPrefs] = useState<Prefs | null>(null);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    try {
      setFailed(false);
      setPrefs(unwrapObject<Prefs>(await api.get("/api/Notification/Preferences")));
    } catch {
      setFailed(true);
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  const save = async (change: Change) => {
    try {
      setPrefs(unwrapObject<Prefs>(await api.put("/api/Notification/Preferences", change)));
      success("Saved", "Your notification choices are updated");
    } catch (err: any) {
      error("Could not save", err?.message || "Please try again");
    }
  };

  if (failed) return <ErrorState title="Couldn't load your notification settings" onRetry={load} />;
  if (!prefs) return <FormSkeleton />;

  return (
    <div className={embedded ? "space-y-4" : "mx-auto max-w-3xl space-y-4"}>
      {!embedded && (
        <PageHeader
          title="Notifications"
          actions={
          <Link to="/notifications" className="text-sm text-primary hover:underline">
            Back to my notifications
          </Link>
          }
        />
      )}
      <p className="text-sm text-muted-foreground">
        {prefs.events.length
          ? "Choose how you hear about the things your admin lets you choose. Security alerts always arrive."
          : "Turn pop-ups on for this device and set your quiet hours. Your admin chooses which alerts you get."}
        {hasPermission("setting.manage") && (
          <>
            {" "}
            <Link to="/settings/notification-rules" className="text-primary hover:underline">Set what everyone gets</Link>
          </>
        )}
      </p>

      <PushDevices onChange={load} />

      {prefs.events.length > 0 && (
      <section aria-label="How you hear about things" className="space-y-2 rounded-[10px] border border-border bg-card px-4 py-3.5">
        <h2 className="text-sm font-semibold text-foreground">How you hear about things</h2>
        <p className="text-xs text-muted-foreground">Push is on for what can't wait (missed calls, new leads, approvals, mentions and customer replies). Email and WhatsApp are on only if you switch them on.</p>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[460px] border-collapse text-[13px]">
            <thead>
              <tr className="text-[11px] uppercase tracking-wide text-muted-foreground">
                <th className="py-1.5 text-left font-semibold">Event</th>
                {CHANNELS.map((c) => (
                  <th key={c.key} className="w-[78px] text-center font-semibold">{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {prefs.events.map((p) => (
                <tr key={p.key} className="border-t border-border/60">
                  <td className="py-2.5 pr-2">{p.label}</td>
                  {CHANNELS.map((c) => (
                    <td key={c.key} className="text-center">
                      <input
                        type="checkbox"
                        checked={p[c.key]}
                        disabled={(c.key === "whatsapp" && !prefs.whatsapp_ready && !p.whatsapp) || (c.key === "push" && !prefs.push_ready && !p.push)}
                        onChange={(e) => save({ events: { [p.key]: { [c.key]: e.target.checked } } })}
                        aria-label={`${p.label} ${c.say}`}
                        className="size-4 accent-primary disabled:opacity-40"
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!prefs.whatsapp_ready && <p className="text-[11.5px] text-muted-foreground">WhatsApp alerts need your mobile number on your profile and a connected business number.</p>}
        {!prefs.push_ready && (
          <p className="text-[11.5px] text-muted-foreground">
            Push notifications aren't set up yet.{" "}
            {hasPermission("setting.manage") ? <Link to="/settings/push" className="text-primary hover:underline">Set them up</Link> : "A Super Admin can set it up in Settings."}
          </p>
        )}
      </section>
      )}

      <section aria-label="Quiet hours and summary" className="space-y-2 rounded-[10px] border border-border bg-card px-4 py-3.5">
        <h2 className="text-sm font-semibold text-foreground">Quiet hours</h2>
        <div className="flex flex-wrap items-center gap-2 text-[13px]">
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={prefs.quiet_hours.enabled} onChange={(e) => save({ quiet_hours: { enabled: e.target.checked } })} className="size-4 accent-primary" />
            Hold push, email and WhatsApp from
          </label>
          <TimeInput compact label="Quiet hours start" value={prefs.quiet_hours.start} onChange={(v) => save({ quiet_hours: { start: v } })} />
          <span className="text-muted-foreground">to</span>
          <TimeInput compact label="Quiet hours end" value={prefs.quiet_hours.end} onChange={(v) => save({ quiet_hours: { end: v } })} />
        </div>
        <p className="text-[11.5px] text-muted-foreground">During quiet hours alerts show in the app only. Missed calls still push while calls are being taken.</p>
        <label className="flex items-center gap-2 pt-1 text-[13px]">
          <input type="checkbox" checked={prefs.morning_summary} onChange={(e) => save({ morning_summary: e.target.checked })} className="size-4 accent-primary" />
          Morning summary email of today's tasks
        </label>
      </section>
    </div>
  );
}
